import { useCallback, useMemo, useRef } from 'react';

import { captureAnalyticsEvent } from '#utils/analytics/analytics-events.js';

import type { UIMessage } from 'ai';
import type {
	WorkbenchAnalyticsSource,
	WorkbenchFeedbackRating,
	WorkbenchSurfaceContext,
} from '#utils/analytics/analytics-events.js';

/** Identifies an instrumented workbench surface and what is on screen around it. */
export interface AgentWorkbenchAnalyticsContext {
	source: WorkbenchAnalyticsSource;
	/**
	 * What the user is looking at, as far as this surface knows it. Merged over
	 * whatever the page context contributed, so a surface holding a value
	 * directly wins over the same value registered for the agent's prompt.
	 */
	surface?: WorkbenchSurfaceContext;
}

/** How a workbench run ended — the reportable slice of the AI SDK's finish event. */
export interface AgentWorkbenchRunOutcome {
	/** The assistant message the run produced; partial when it failed part-way. */
	message: UIMessage;
	isAbort: boolean;
	isDisconnect: boolean;
	isError: boolean;
}

/** A rating a user gave one assistant reply. */
export interface AgentWorkbenchFeedback {
	/** The rated assistant message. */
	messageId: string;
	/** Conversation the reply belongs to; undefined on a thread with no backend id yet. */
	conversationId: string | undefined;
	rating: WorkbenchFeedbackRating;
}

/** Emits one thread's workbench analytics events. */
export interface AgentWorkbenchAnalyticsRecorder {
	/**
	 * Records a message the user submitted, and opens the window that the next
	 * `recordRunSettled` closes.
	 *
	 * @param messages - The thread's messages, ending with the submitted one.
	 */
	recordMessageSent: (messages: readonly UIMessage[]) => void;
	/**
	 * Records the outcome of the run started by the last `recordMessageSent`,
	 * closing that message's window. A run with no such message is ignored, as is
	 * one the user stopped or that lost its stream: neither answered the message,
	 * and timing either would misreport how long an answer takes.
	 *
	 * @param outcome - How the run ended.
	 */
	recordRunSettled: (outcome: AgentWorkbenchRunOutcome) => void;
	/**
	 * Records a rating a user gave one reply. Independent of the send/settle
	 * pair: a reply can be rated long after its run, or never.
	 *
	 * @param feedback - The rating and the reply it applies to.
	 */
	recordFeedback: (feedback: AgentWorkbenchFeedback) => void;
}

/**
 * Total characters across a message's text parts, ignoring every other part
 * kind (tool calls, attachments, narration data).
 *
 * @param message - The message to measure.
 * @returns The character count; 0 for a text-free message.
 */
export function measureMessageTextLength(message: UIMessage): number {
	return message.parts
		.filter((part): part is { type: 'text'; text: string } => part.type === 'text')
		.reduce((total, part) => total + part.text.length, 0);
}

/**
 * Number of user messages in a thread — the 1-based position of the newest one.
 *
 * @param messages - The thread's messages.
 * @returns The count of messages with the `user` role.
 */
export function countUserMessages(messages: readonly UIMessage[]): number {
	return messages.filter((message) => message.role === 'user').length;
}

/**
 * Emits the workbench analytics events for one thread, pairing each reply with the
 * message that prompted it.
 *
 * Only metadata is captured: message and reply text never leave the browser, so
 * the design questions customers ask stay out of analytics.
 *
 * @param context - The surface's analytics identity; undefined leaves the surface uninstrumented, emitting nothing.
 * @returns Recorders for a submitted message and for the run it started.
 */
export function useAgentWorkbenchAnalytics(
	context: AgentWorkbenchAnalyticsContext | undefined,
): AgentWorkbenchAnalyticsRecorder {
	// An outcome is only reported against a send this thread observed, so runs
	// driven from elsewhere — the ingest narration, another session — are skipped
	// rather than reported with a latency measured from nothing.
	const sentAtRef = useRef<number | undefined>(undefined);

	const recordMessageSent = useCallback(
		(messages: readonly UIMessage[]): void => {
			if (context === undefined) {
				return;
			}
			sentAtRef.current = Date.now();
			const submitted = messages.findLast((message) => message.role === 'user');
			captureAnalyticsEvent('chat_message_sent', {
				...context.surface,
				source: context.source,
				message_length: submitted === undefined ? 0 : measureMessageTextLength(submitted),
				message_index: countUserMessages(messages),
			});
		},
		[context],
	);

	const recordRunSettled = useCallback(
		({ message, isAbort, isDisconnect, isError }: AgentWorkbenchRunOutcome): void => {
			const sentAt = sentAtRef.current;
			if (context === undefined || sentAt === undefined) {
				return;
			}
			sentAtRef.current = undefined;
			if (isAbort || isDisconnect) {
				return;
			}
			captureAnalyticsEvent('chat_response_received', {
				...context.surface,
				source: context.source,
				latency_ms: Date.now() - sentAt,
				response_length: measureMessageTextLength(message),
				is_error: isError,
			});
		},
		[context],
	);

	const recordFeedback = useCallback(
		({ messageId, conversationId, rating }: AgentWorkbenchFeedback): void => {
			if (context === undefined) {
				return;
			}
			captureAnalyticsEvent('chat_feedback_submitted', {
				...context.surface,
				source: context.source,
				rating,
				message_id: messageId,
				...(conversationId !== undefined && { conversation_id: conversationId }),
			});
		},
		[context],
	);

	return useMemo(
		() => ({ recordMessageSent, recordRunSettled, recordFeedback }),
		[recordMessageSent, recordRunSettled, recordFeedback],
	);
}
